import React, { useEffect, useRef, useCallback, useMemo } from 'react';

import { usePrevious } from '@gogame/hooks-ts';

import HierarchyContext from './HierarchyContext';
import { getIn } from './utils';

const defaultParams = (values, depends) => {
  return depends.reduce((result, dep) => {
    let lastKey = dep.match(/[\w-]+$/g);
    return { ...result, [lastKey]: getIn(values, dep, undefined) };
  }, {});
};

const EMPTY_DEPENDS = [];
const SOURCE_CHANGE_TTL = 10;

export const useHierarchyArea = ({
  name,
  source,
  depends = EMPTY_DEPENDS,
  active = true,
  params = defaultParams,
  onDataLoad,
  normalizeValue,
  keepStateOnUnmount = false,
}) => {
  const { registerArea, unregisterArea, state, update } =
    React.useContext(HierarchyContext);
  const keepStateOnUnmountRef = React.useRef(keepStateOnUnmount);

  keepStateOnUnmountRef.current = keepStateOnUnmount;

  const { data, loading, error } = useMemo(() => {
    return (
      getIn(state, name) || {
        loading: Boolean(active && source),
      }
    );
  }, [state, name, active, source]);

  const prevActive = usePrevious(active);
  const _sourceChangeTtlRef = useRef(SOURCE_CHANGE_TTL);
  const _registeredRef = useRef(false);

  const reload = useCallback(() => update(name), [update, name]);

  const handleFetch = useCallback(
    async (values, state) => {
      return source(params(values, depends), state);
    },
    [source, params, depends]
  );

  useEffect(() => {
    if (!source) {
      _registeredRef.current = false;
      return;
    }

    if (prevActive) {
      unregisterArea(name);
    }

    if (active) {
      registerArea(name, { depends, fetch: handleFetch, normalizeValue });
      _registeredRef.current = true;
    } else {
      _registeredRef.current = false;
    }
  }, [active, source, depends.toString()]);

  useEffect(() => {
    if (!_sourceChangeTtlRef.current--) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line
        console.error(
          `${name}: source has changed more then ${SOURCE_CHANGE_TTL} times.`
        );
      }
    }
  }, [source]);

  useEffect(() => {
    if (onDataLoad && data) {
      onDataLoad({ data });
    }
  }, [data, error]);

  useEffect(
    () => () => {
      _registeredRef.current &&
        !keepStateOnUnmountRef.current &&
        unregisterArea(name);
    },
    []
  );

  return useMemo(
    () => ({ data, loading, error, active, reload }),
    [data, loading, error, active, reload]
  );
};

export const HierarchyArea = ({
  children,
  name,
  source,
  depends,
  active,
  params,
  onDataLoad,
  normalizeValue,
}) => {
  const areaBag = useHierarchyArea({
    name,
    source,
    depends,
    active,
    params,

    onDataLoad,
    normalizeValue,
  });

  if (typeof children === 'function') {
    return children({
      area: areaBag,
    });
  }

  return children || null;
};

export default HierarchyArea;
